import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "../api/client.js";
import { useAuth } from "./AuthContext.jsx";

/** Dónde quedan los clientes elegidos entre sesiones (una sola llave para todo el módulo). */
export const TENANT_STORAGE_KEY = "finanzas.selectedTenant";

const FinanzasTenantContext = createContext(null);

/**
 * Lee la preferencia guardada de clientes.
 *
 * Acepta la forma vieja —un id suelto, de cuando el módulo trabajaba sobre UN
 * cliente— para que nadie pierda su selección al actualizar: un string se lee
 * como la lista de un elemento.
 */
export function readStoredTenants() {
  let crudo;
  try {
    crudo = localStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return [];
  }
  if (!crudo) return [];
  if (!crudo.startsWith("[")) return [crudo];
  try {
    const lista = JSON.parse(crudo);
    return Array.isArray(lista) ? lista.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
}

/**
 * El ALCANCE sobre el que trabaja TODO el módulo de Finanzas: qué CLIENTES.
 *
 * Antes cada pantalla cargaba su propia lista de tenants y tenía su propio
 * selector: pasar de Panorama a Movimientos y a Arqueo con el cliente "Cecilia"
 * era elegirlo tres veces. Ahora el alcance vive UNA sola vez, acá, y cada
 * pantalla lo ofrece leyendo este mismo estado, desde la chapa del encabezado
 * (`TenantSelector`) — que es lo que antes no se podía hacer sin tener siete
 * estados distintos peleándose.
 *
 * Las UNIDADES de negocio no se eligen: son las de los clientes elegidos, todas.
 * Tuvieron su propio selector y era decir dos veces lo mismo — quien elige
 * "Cecilia y Alejandro" quiere ver los dos libros, y quien quiere uno solo
 * elige ese cliente. Un cliente con dos unidades las muestra juntas, que es
 * como se mira su cartera.
 *
 * Y el alcance es un CONJUNTO de clientes, no uno solo: la flota está partida en
 * tres libros (Cecilia, Kevin y Valentina, Luis) y la pregunta de todos los días
 * —"¿cómo venimos entre todos?", "¿qué motos tengo entre Cecilia y Kevin?"— no
 * se puede contestar mirando un libro por vez. La lista vacía significa TODOS:
 * así un cliente nuevo entra solo, sin que haya que volver a marcarlo.
 *
 * El provider cuelga de la ruta `/admin/finanzas`, así que sobrevive a la
 * navegación entre pantallas de la sección: clientes y unidades se piden una
 * vez, no una por página.
 *
 * Para un owner no hay clientes que elegir: trabaja sobre el suyo y el backend
 * lo resuelve por el token.
 */
export function FinanzasTenantProvider({ children }) {
  const { isMaster } = useAuth();

  const [tenants, setTenants] = useState([]);
  const [tenantIds, setTenantIdsState] = useState(() => (isMaster ? readStoredTenants() : []));
  // `ready` separa "todavía no sé qué clientes hay" de "no hay ninguno": sin
  // esa diferencia las pantallas no saben si dejar el spinner o rendirse.
  const [ready, setReady] = useState(!isMaster);
  const [error, setError] = useState(null);

  const [units, setUnits] = useState([]);
  const [unitsReady, setUnitsReady] = useState(false);
  // De qué alcance son las unidades que hay en mano. Ver `unitScope`.
  const [unitsFor, setUnitsFor] = useState(null);
  const unitsSeq = useRef(0);

  useEffect(() => {
    if (!isMaster) {
      setTenants([]);
      setTenantIdsState([]);
      setError(null);
      setReady(true);
      return;
    }
    let cancelled = false;
    setReady(false);
    api
      .getTenants()
      .then((list) => {
        if (cancelled) return;
        setTenants(list);
        // Lo guardado se valida contra la lista real: un id de otra sesión (o de
        // un cliente ya borrado) mandaría a pedir datos de un tenant que no
        // existe y la pantalla entera se caería con un 404. Los que sobreviven
        // se conservan; si no queda ninguno, el alcance es "todos".
        const vivos = readStoredTenants().filter((id) => list.some((t) => t.id === id));
        setTenantIdsState(vivos);
        setError(null);
        setReady(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isMaster]);

  useEffect(() => {
    if (!isMaster) return;
    try {
      localStorage.setItem(TENANT_STORAGE_KEY, JSON.stringify(tenantIds));
    } catch {
      /* modo privado o storage lleno: la preferencia no se guarda, nada más */
    }
  }, [isMaster, tenantIds]);

  const setTenantIds = useCallback((ids) => {
    setTenantIdsState(Array.isArray(ids) ? ids : []);
  }, []);

  /** Los clientes que de verdad se consultan: la lista vacía es TODOS. */
  const scopedTenants = useMemo(() => {
    if (!isMaster) return [];
    const elegidos = tenantIds.length > 0 ? tenantIds : tenants.map((t) => t.id);
    // Se respeta el orden de la lista de clientes, no el de los clics: es el
    // mismo orden en el que se ven en el selector y en las tablas.
    return tenants.filter((t) => elegidos.includes(t.id)).map((t) => t.id);
  }, [isMaster, tenantIds, tenants]);

  /** Lo que se le pasa a la API: el master manda sus clientes, el owner nada. */
  const tenantParam = isMaster ? scopedTenants.join(",") : undefined;

  // Master sin ningún cliente resuelto (todavía sin lista, o sin clientes):
  // no hay a quién consultar y las pantallas no pueden quedarse girando.
  const blocked = isMaster && scopedTenants.length === 0;

  const loadUnits = useCallback(() => {
    // Mientras no se sepa qué clientes hay, el alcance no está "resuelto y
    // vacío": está sin resolver. Marcarlo listo acá le decía a las pantallas
    // que no había unidades un instante antes de que llegaran, y las que
    // dependen de eso (el modo bloque de la planilla) se apagaban solas.
    if (!ready) return;
    if (blocked) {
      setUnits([]);
      setUnitsFor(tenantParam ?? "");
      setUnitsReady(true);
      return;
    }
    const seq = ++unitsSeq.current;
    setUnitsReady(false);
    api
      .finGetUnits(tenantParam)
      .then((list) => {
        if (seq !== unitsSeq.current) return;
        setUnits(list);
        setUnitsFor(tenantParam ?? "");
        setUnitsReady(true);
      })
      .catch((e) => {
        if (seq !== unitsSeq.current) return;
        setUnits([]);
        setUnitsFor(tenantParam ?? "");
        setUnitsReady(true);
        setError(e.message);
      });
  }, [ready, blocked, tenantParam]);

  useEffect(loadUnits, [loadUnits]);


  const value = useMemo(() => {
    const porId = new Map(tenants.map((t) => [t.id, t]));
    /* Las unidades que hay en mano son las del alcance ANTERIOR hasta que
       llega la lista nueva: se siguen mostrando —con `unitsReady` en false, que
       es lo que dice "cargando"— pero NO se pueden consultar. Sin este corte,
       `unitParam` nombraba unidades de otro cliente y cualquier pantalla que
       arrancara una consulta en esa ventana la pedía con el alcance viejo; en
       la planilla es peor que una consulta de más, porque materializa los
       cargos del día de una flota que nadie pidió. */
    const alcanceResuelto = unitsFor === (tenantParam ?? "");
    const unitScope = alcanceResuelto ? units.map((u) => u.id) : [];
    /* De cuántos clientes son los números que están en pantalla. Es lo que
       decide si las tablas muestran la columna "Cliente": con uno solo, repetir
       su nombre en cada fila es ruido; con dos, sin ella no se sabe de quién es
       cada moto. Se mide sobre las UNIDADES cargadas y no sobre los clientes
       elegidos: un cliente sin unidades no pone números en pantalla. */
    const tenantsInScope = new Set(units.map((u) => u.tenant_id));

    return {
      isMaster,
      tenants,
      /** Selección explícita del usuario; vacía = todos los clientes. */
      tenantIds,
      setTenantIds,
      /** Los que de verdad se consultan (la lista vacía ya resuelta a "todos"). */
      scopedTenants,
      allTenantsSelected: isMaster && tenantIds.length === 0,
      tenantParam,
      /** De quién son los números, para la chapa del encabezado. */
      tenantLabel: tenantLabelFor({ isMaster, tenants: scopedTenants.map((id) => porId.get(id)) }),
      /** Nombre del cliente de una fila (las tablas lo usan como columna). */
      tenantNameById: (id) => porId.get(id)?.name ?? null,

      units,
      /* "Listo" es que lo que hay en mano sea del alcance PEDIDO, no que haya
         terminado una carga cualquiera. `tenantParam` se deriva en el render
         mismo del clic, pero la nueva carga la dispara un efecto: entre los dos
         hay un commit en el que el alcance ya es el nuevo y `unitsReady` todavía
         decía `true` — y ahí las pantallas pintaban los números del cliente
         anterior con el nombre del nuevo, que es justo lo que se vino a evitar.
         Derivado acá, las dos cosas cambian en el mismo render. */
      unitsReady: unitsReady && alcanceResuelto,
      reloadUnits: loadUnits,
      /** Las unidades que se consultan: TODAS las de los clientes elegidos. */
      unitScope,
      /** Lo que viaja en la URL: "a,b,c" (vacío = no hay nada que consultar). */
      unitParam: unitScope.join(","),
      /** Con más de un cliente en pantalla las tablas rotulan de quién es cada fila. */
      multiTenant: tenantsInScope.size > 1,

      ready,
      error,
      blocked,
    };
  }, [
    isMaster,
    tenants,
    tenantIds,
    setTenantIds,
    scopedTenants,
    tenantParam,
    units,
    unitsReady,
    unitsFor,
    loadUnits,
    ready,
    error,
    blocked,
  ]);

  return (
    <FinanzasTenantContext.Provider value={value}>{children}</FinanzasTenantContext.Provider>
  );
}

/**
 * Cómo se nombra el alcance en la chapa del encabezado.
 *
 * Con uno, su nombre. Con dos, los dos —"Cecilia y Kevin" es la pregunta que se
 * hace el usuario, y contestarle "2 clientes" le esconde justamente el dato—.
 * De tres en adelante ya no entra en la chapa y se cuenta.
 */
export function tenantLabelFor({ isMaster, tenants }) {
  if (!isMaster) return null;
  const nombres = (tenants ?? []).filter(Boolean).map((t) => t.name);
  if (nombres.length === 0) return null;
  if (nombres.length === 1) return nombres[0];
  if (nombres.length === 2) return `${nombres[0]} y ${nombres[1]}`;
  return `${nombres.length} clientes`;
}

export function useFinanzasTenant() {
  const ctx = useContext(FinanzasTenantContext);
  if (!ctx) {
    throw new Error("useFinanzasTenant() necesita un <FinanzasTenantProvider> por encima");
  }
  return ctx;
}
